/**
 * reCAPTCHA v3 for the e-mail verification step. The API scores the token before it will say
 * whether an address is the right one (SharePass-Infra `pull-secret/funnel.js`, "Captcha
 * required."), so without one the reveal page can never get past that question.
 *
 * Loaded on demand rather than on every reveal: only secrets protected by e-mail need it, and the
 * script watches the page it is loaded on.
 */
const SITE_KEY = (import.meta.env.VITE_RECAPTCHA_KEY || '6Le_zDIeAAAAAM7B4Mqb4sPyREULQULJPaBffKPq').trim();

const SCRIPT_ID = 'sharepass-recaptcha';

interface Grecaptcha {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
    interface Window {
        grecaptcha?: Grecaptcha;
    }
}

let loading: Promise<Grecaptcha | null> | null = null;

const loadScript = (): Promise<Grecaptcha | null> => {
    if (window.grecaptcha) return Promise.resolve(window.grecaptcha);

    loading ??= new Promise<Grecaptcha | null>((resolve) => {
        const existing = document.getElementById(SCRIPT_ID);
        const script = existing instanceof HTMLScriptElement ? existing : document.createElement('script');

        script.id = SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
        script.async = true;
        script.onload = () => resolve(window.grecaptcha ?? null);
        script.onerror = () => resolve(null);

        if (!existing) document.head.appendChild(script);
    });

    return loading;
};

/**
 * A token for `action`, or null when reCAPTCHA cannot run (offline, blocked by an extension). The
 * caller sends the request anyway: the API decides, and a null token simply fails its check.
 */
export const getCaptchaToken = async (action: string): Promise<string | null> => {
    try {
        const grecaptcha = await loadScript();
        if (!grecaptcha) return null;

        await new Promise<void>((resolve) => grecaptcha.ready(resolve));
        return await grecaptcha.execute(SITE_KEY, { action });
    } catch {
        return null;
    }
};
